import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup, AbstractControl } from '@angular/forms';
import { StorageService } from 'src/app/shared/services/storage.service';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { BaseReservationComponent, AvailabilityType } from './base-reservation.component';
import { BuildingService } from 'src/app/shared/services/rest/building.service';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'src/app/shared/services/message.service';
import { AvailableDevice } from 'src/app/shared/models/available-device';
import { Occupancy } from 'src/app/shared/models/occupancy';
import { MessageType } from 'src/app/shared/models/message';
import { HttpErrorResponse } from '@angular/common/http';
import { DeviceService, IAvailableDevicesParams } from 'src/app/shared/services/rest/device.service';
import { FORM_VALUE_SEPARATOR } from 'src/app/shared/components/multi-dropdown/multi-dropdown.component';
import { SearchForm } from './models/search-form';
import { IDeviceSearchForm } from './models/i-device-search-form';
import { AllocationTableModel } from './components/room-allocation-table/room-allocation-desktop.component';
import { SessionSettings } from 'src/app/shared/models/client-settings.model';
import { IRoomScheduleInfo } from './components/room-allocation-table/room-allocation-table.component';
import { ResourceType } from 'src/app/shared/models/resource-type';
import { ResourceTableRow } from 'src/app/shared/models/resource-table-row';

@Component({
  selector: 'app-device-reservation',
  templateUrl: './device-reservation.component.html'
})
export class DeviceReservationComponent extends BaseReservationComponent<IDeviceSearchForm> implements OnInit {

  /**
   * Api to device-endpoints
   *
   * @protected
   * @type {DeviceService}
   * @memberof DeviceReservationComponent
   */
  protected device: DeviceService;

  get resourceType(): ResourceType {
    return ResourceType.Gerät;
  }

  /**
   * constant panel id for search panel
   *
   * @memberof DeviceReservationComponent
   */
  readonly accordionSearchPanel = 'accordionSearchPanel';

  /**
 * constant panel id for result panel
 *
 * @memberof DeviceReservationComponent
 */
  readonly accordionResultPanel = 'accordionResultPanel';

  allocations: AvailableDevice[] = [];

  /**
   * Angular Form model
   *
   * @type {FormGroup}
   * @memberof BaseReservationComponent
   */
  searchFormGroup: FormGroup;

  constructor(
    storage: StorageService,
    fb: FormBuilder,
    building: BuildingService,
    router: Router,
    activatedRoute: ActivatedRoute,
    translate: TranslateService,
    messages: MessageService,
    device: DeviceService,
    protected readonly cdr: ChangeDetectorRef,
    ) {
    super(storage, fb, building, router, activatedRoute, translate, messages, cdr);

    this.device = device;
  }

  toggleInfoTextCollapse() {
    this.isCollapsedInfoText = !this.isCollapsedInfoText;
    this.storage.updateClientSettings((settings) => {
      settings.roomSearchComponent.infoTextCollapsed = this.isCollapsedInfoText;
    });
  }

  ngOnInit() {
    const now = DateHelper.getCurrentDate();

    // set some selection defaults
    this.searchForm.setValue('date', now);
    this.searchForm.setValue('showAvailableTimeFrom', (now.getHours() + 1) * 60);
    this.searchForm.setValue('showAvailableTimeTo', (now.getHours() + 2) * 60);
    this.searchForm.setValue('showAvailableHours', 60);

    this.storage.clientSettings.subscribe((data) => {
      this.isCollapsedInfoText = data.roomSearchComponent.infoTextCollapsed;
    });

    // restore settings from session-storage
    this.restoreSessionData();

    this.attachEvents();
  }

  onAttachEvents() {
    // safe this settings to session-storage to prevent loss on reload
    this.searchForm.formGroup.valueChanges.subscribe((_: any) => {
      this.updateSessionData();
    });
  }

  protected executeSearch() {

    this.isLoading = true;

    // open panel (setTimeout: workaround for animation issue in IE and Edge)
    window.setTimeout(() => { this.accordion.openSingle(this.accordionResultPanel); }, 1);

    const params = this.getAvailableDevicesParams();
    this.currentDate = params.availableFrom;

    this.device.getAvailableDevices(params).toPromise().then(
      (data: AvailableDevice[]) => {
        const list = new Array<AvailableDevice>();
        (<AvailableDevice[]>data).forEach(r => {
          const occs = new Array<Occupancy>();
          if (r.Occupancies != null) {
            r.Occupancies.forEach(o => {
              occs.push(Occupancy.create(o));
            });
          }
          const obj = AvailableDevice.create(r);
          obj.Occupancies = occs;
          obj.meta = this.device.getReservabilityToken(obj, this.currentDate);

          // subscribe warnings (mainly required because of translation)
          const observer = this.device.getFirstWarning(obj, this.currentDate);
          if (observer != null) {
            observer.subscribe((message: string) => {
              obj.warning = message;
            });
          }

          list.push(obj);
        });

        // assign prepared data
        this.allocations = list;
        this.allocationTableModel = this.mapAllocationTableModel(
          list,
          DateHelper.StartOfDay(this.currentDate),
          DateHelper.EndOfDay(this.currentDate)
        );

        this.isLoading = false;
      },
      (error: HttpErrorResponse) => {
        console.error('error on loading result');
        console.error(error);

        this.translate.get('DATA_LODING_ERROR_GENERAL').subscribe(
          (text: string) => { this.messages.add(text, MessageType.Error); }
        );

        this.isLoading = false;
      });
  }

  protected createForm() {
    this.searchForm = new SearchForm(this.fb.group({ // <-- the parent FormGroup
      number: [''],
      date: [<Date>null, Validators.required],
      showAvailable: false,
      showAvailableType: AvailabilityType.absolut,
      showAvailableHours: 0,
      showAvailableTimeFrom: 0,
      showAvailableTimeTo: 0
    }));
    this.searchFormGroup = this.searchForm.formGroup;
  }

  /**
   * Create AllocationTableModel from AvailableDevice array
   *
   * @private
   * @param {AvailableDevice[]} devices
   * @param {Date} minDate
   * @param {Date} maxDate
   * @returns {AllocationTableModel}
   * @memberof DeviceReservationComponent
   */
  private mapAllocationTableModel(devices: AvailableDevice[], minDate: Date, maxDate: Date): AllocationTableModel {
    const model = new AllocationTableModel();

    const displayFields = [{
      columnName: 'Device',
      captionLocaleKey: 'DEVICENUMBER',
      columnSpan: 1,
      showFromScreenSize: 1
    }];

    model.resourceType = ResourceType.Gerät;
    model.additionalColums = displayFields.map(e => ResourceTableRow.create(e, null));

    this.translate.get('DEVICE_CALENDAR').subscribe((text: string) => {
      model.calendarButtonText = text;
    });

    this.translate.get('DEVICE').subscribe((text: string) => {
      model.mainColumnTitle = text;
    });

    model.displayItems = devices.map(ad => {
      const di = AvailableDevice.mapDisplayItem(ad, this.translate.get('OCCUPIED'), minDate, maxDate);

      // multi data row generation
      di.additionalColumsData = displayFields
        .map(fieldDef => ResourceTableRow.create(fieldDef, ad !== null ? ad[fieldDef.columnName] : null));

      return di;
    });

    return model;
  }

  /**
   * After reservation saved hook
   *
   * @param {string} location
   * @memberof DeviceReservationComponent
   */
  onAfterReservationSaved(location: string) {
    // open confirmation
    const parts = location.split('/');
    this.router.navigate(['/my/confirmation/' + Number(parts[parts.length - 1])]);
  }

  /**
  * Handel openRoomInfo callback of room-allocation-item
  *
  * @param {number} roomId
  * @memberof RoomAllocationComponent
  */
  onOpenSchedule(info: IRoomScheduleInfo) {
    this.activeItemId = info.roomId;
    this.activeRessourceInfo = info;
    this.translate.get('DEVICE').subscribe((text: string) => {
      this.modalRoomSchedule.title = text + ' ' + info.designation;
    });
    this.modalRoomSchedule.openModal();
  }

  /**
   * Map search form data to search-params object
   *
   * @private
   * @returns {IAvailableDevicesParams}
   * @memberof DeviceReservationComponent
   */
  private getAvailableDevicesParams(): IAvailableDevicesParams {
    const params: IAvailableDevicesParams = {
      idDevice: [],
      availableFrom: DateHelper.parseIsoDateString(this.searchForm.getValue('date')),
      availableTo: DateHelper.parseIsoDateString(this.searchForm.getValue('date')),
      availableOnly: !!this.searchForm.getValue('showAvailable'),
      timespan: !!this.searchForm.getValue('showAvailable') ?
        Number(this.searchForm.getValue('showAvailableHours')) : null,
      ressourceNumber: this.searchForm.getValue('number')
        .split(FORM_VALUE_SEPARATOR)
        .map(r => r.trim())
    };

    if (this.searchForm.getValue('showAvailable') && this.searchForm.getValue('showAvailableType') === AvailabilityType.absolut) {
      params.timespan = null;
      const showAvailableTimeFrom = Number(this.searchForm.getValue('showAvailableTimeFrom'));
      const showAvailableTimeTo = Number(this.searchForm.getValue('showAvailableTimeTo'));

      if (showAvailableTimeFrom !== NaN && showAvailableTimeTo !== NaN) {
        params.availableFrom.setHours(Math.floor(showAvailableTimeFrom / 60), showAvailableTimeFrom % 60, 0, 0);

        const to = showAvailableTimeTo / 60;
        params.availableTo.setHours(Math.floor(to), showAvailableTimeTo % 60, 0, 0);
        if (to === 24) {
          params.availableTo = new Date(params.availableTo.getTime() - 1000); // remove one sec (ie fix)
        }
      }

    } else {
      params.availableFrom.setHours(0, 0, 0);
      params.availableTo.setHours(23, 59, 59, 999);
    }

    return params;
  }

  protected getSessionSettingsKey(): keyof SessionSettings {
    return 'deviceSearchForm';
  }

}
